import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { 
  Eye, 
  EyeOff, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Shield,
  User,
  Users,
  Send,
  Ban
} from 'lucide-react';

// Button component to request patient view access
export function RequestPatientViewButton({ patientId, patientName, viewerId, viewerName }) {
  const { api, user } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user already has access
  const checkAccess = async () => {
    try {
      const response = await api.get('/patient-view-approvals/my-approved');
      const approved = response.data.find(a => a.patient_id === patientId);
      setHasAccess(approved);
    } catch (error) {
      console.error('Failed to check access:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) {
      checkAccess();
    }
  }, [patientId]);

  if (loading) {
    return (
      <Button variant="outline" disabled className="rounded-full" size="sm">
        <Eye className="w-4 h-4 mr-2 animate-pulse" />
        Checking...
      </Button>
    );
  }

  if (hasAccess) {
    return (
      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
        <Eye className="w-3 h-3 mr-1" />
        View Access Granted
      </Badge>
    );
  }

  const handleRequestAccess = async () => {
    setSubmitting(true);
    try {
      await api.post('/patient-view-approvals/request', {
        patient_id: patientId,
        viewer_id: viewerId || user.id,
        reason: reason.trim() || undefined
      });
      toast.success('View access request submitted');
      setShowDialog(false);
      setReason('');
      checkAccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button 
        variant="outline" 
        className="rounded-full border-blue-200 text-blue-600 hover:bg-blue-50"
        onClick={() => setShowDialog(true)}
        size="sm"
        data-testid="request-view-access-btn"
      >
        <Eye className="w-4 h-4 mr-2" />
        Request View Access
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Request Patient View Access
            </DialogTitle>
            <DialogDescription>
              Request permission to view <strong>{patientName}</strong>&apos;s records.
              A Lead Therapist or Clinical Lead will review your request.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (optional)</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why do you need to view this patient's records?"
                rows={3}
                data-testid="view-request-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleRequestAccess}
              disabled={submitting}
              data-testid="submit-view-request-btn"
            >
              {submitting ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Panel for approvers to view and manage pending requests
export function PatientViewApprovalPanel() {
  const { api, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  // Check if user can approve requests
  const jobTitle = (user?.job_title || '').toLowerCase();
  const isLeadTherapist = ['lead', 'senior', 'head', 'chief', 'principal'].some(term => jobTitle.includes(term));
  const canApprove = ['director', 'managing_director', 'clinical_lead'].includes(user?.role) || 
                     user?.email?.toLowerCase() === 'admin@icrcl.co.uk' ||
                     isLeadTherapist;

  const fetchRequests = async () => {
    if (!canApprove) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/patient-view-approvals/pending');
      setRequests(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [canApprove]);

  const handleApprove = async (approvalId) => {
    setProcessing(approvalId);
    try {
      await api.put(`/patient-view-approvals/${approvalId}/approve`);
      toast.success('Patient view access approved');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleRevoke = async (approvalId) => {
    setProcessing(approvalId);
    try {
      await api.put(`/patient-view-approvals/${approvalId}/revoke`);
      toast.success('Patient view access revoked');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    } finally {
      setProcessing(null);
    }
  };

  if (!canApprove) return null;
  if (loading) return null;
  if (requests.length === 0) return null;

  return (
    <Card className="card-icrcl border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <CardTitle className="text-lg text-blue-800">
              Patient View Requests ({requests.length})
            </CardTitle>
            <CardDescription className="text-blue-600">
              Therapists requesting permission to view other therapists&apos; patients
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {requests.map((request) => (
            <div
              key={request.id}
              className="p-4 bg-white rounded-lg border border-blue-100"
              data-testid={`view-request-${request.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="font-medium text-slate-900">
                      {request.viewer_name}
                    </span>
                    <span className="text-sm text-slate-500">wants to view</span>
                    <span className="font-medium text-slate-900">
                      {request.patient_name}
                    </span>
                  </div>
                  {request.owner_therapist_name && (
                    <p className="text-sm text-slate-600 mb-2">
                      Primary therapist: <strong>{request.owner_therapist_name}</strong>
                    </p>
                  )}
                  {request.reason && (
                    <div className="bg-slate-50 p-2 rounded text-sm text-slate-600">
                      <strong>Reason:</strong> {request.reason}
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-2">
                    Requested: {new Date(request.created_at).toLocaleString('en-GB')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => handleRevoke(request.id)}
                    disabled={processing === request.id}
                    data-testid={`deny-view-request-${request.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Deny
                  </Button>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleApprove(request.id)}
                    disabled={processing === request.id}
                    data-testid={`approve-view-request-${request.id}`}
                  >
                    {processing === request.id ? (
                      <Clock className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-1" />
                    )}
                    Approve
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Management page component for viewing all approvals
export function PatientViewApprovalsManagement() {
  const { api, user } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const fetchApprovals = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await api.get('/patient-view-approvals/all', { params });
      setApprovals(response.data || []);
    } catch (error) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovals();
  }, [filter]);

  const handleRevoke = async (approvalId) => {
    setProcessing(approvalId);
    try {
      await api.put(`/patient-view-approvals/${approvalId}/revoke`);
      toast.success('Access revoked');
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'revoked':
        return <Badge className="bg-red-100 text-red-700"><Ban className="w-3 h-3 mr-1" />Revoked</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Card className="card-icrcl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Cross-Therapist Patient Access</CardTitle>
            <CardDescription>
              Manage who can view which patients across therapist boundaries
            </CardDescription>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Clock className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : approvals.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No patient view approvals found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {approvals.map((approval) => (
              <div
                key={approval.id}
                className="p-4 bg-slate-50 rounded-lg flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-medium">{approval.viewer_name}</span>
                    <span className="text-slate-400">→</span>
                    <span className="font-medium">{approval.patient_name}</span>
                    {getStatusBadge(approval.status)}
                  </div>
                  <div className="text-sm text-slate-500">
                    {approval.owner_therapist_name && (
                      <span>Primary: {approval.owner_therapist_name} • </span>
                    )}
                    Requested: {new Date(approval.created_at).toLocaleDateString('en-GB')}
                    {approval.approved_at && ` • Approved: ${new Date(approval.approved_at).toLocaleDateString('en-GB')}`}
                    {approval.approved_by_name && ` by ${approval.approved_by_name}`}
                  </div>
                </div>
                {approval.status === 'approved' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => handleRevoke(approval.id)}
                    disabled={processing === approval.id}
                  >
                    <Ban className="w-4 h-4 mr-1" />
                    Revoke Access
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default RequestPatientViewButton;
